import React, { useState, useEffect } from 'react';
import { Theme, Tabs, Tab, Button, alpha } from '@mui/material';
import { getStyleVariables } from 'styles/vars';
import { DocumentTypes } from 'types';
import { PDFManagerFactory, DocumentHighlight } from 'pdftron';
import { getSeparationHidden, getTextProcessStatus } from 'store';
import { useSelector } from 'react-redux';
import OptionalContentList from './OptionalContentList';
import TabLabel from './TabLabel';
import GVTabPanel from '../../common/GVTabPanel/GVTabPanel';
import { makeStyles } from 'tss-react/mui';
import toggleSeparations from 'utils/toggleSeparations';
import OverprintSwitch from './OverprintSwitch';
import styles from './OptionalContent.module.scss';

const useStyles = makeStyles()((theme: Theme) => {
  const styleVariables = getStyleVariables(theme);
  return {
    listBorder: {
      border: `1px solid ${alpha(theme.palette.primary.dark, 0.25)}`,
    },
    scrollBar: styleVariables.scrollBar,
  };
});

enum OptionalContentTabIndexes {
  source = 0,
  new = 1,
}

const OptionalContent = () => {
  const { classes } = useStyles();
  const [value, setValue] = useState(OptionalContentTabIndexes.source);
  const documentProcessed = useSelector(getTextProcessStatus);
  const separationHidden = useSelector(getSeparationHidden);
  const [sourceSeparationsEnabled, setSourceSeparationsEnabled] = useState(separationHidden);
  const [targetSeparationsEnabled, setTargetSeparationsEnabled] = useState(separationHidden);

  const handleSeparationsToggle = (enabled: boolean, documentType: DocumentTypes) => {
    toggleSeparations(enabled, documentType);
    if (documentType === DocumentTypes.source) {
      setSourceSeparationsEnabled(enabled);
    } else {
      setTargetSeparationsEnabled(enabled);
    }
  };

  useEffect(() => {
    const sourceInstance = PDFManagerFactory.getViewer(DocumentTypes.source)?.instance;
    const targetInstance = PDFManagerFactory.getViewer(DocumentTypes.target)?.instance;
    if (!sourceInstance || !targetInstance) return;
    const sourceDoc = new DocumentHighlight(sourceInstance);
    const targetDoc = new DocumentHighlight(targetInstance);

    if (value === OptionalContentTabIndexes.source) {
      sourceDoc.addDocumentTransition();
      targetDoc.removeDocumentTransition();
    } else {
      targetDoc.addDocumentTransition();
      sourceDoc.removeDocumentTransition();
    }
  }, [value]);

  const handleChange = (_event: React.ChangeEvent<{}>, newValue: OptionalContentTabIndexes) => {
    setValue(newValue);
  };

  return (
    <div className={styles.panel} data-testid="layer-controls">
      <Tabs
        indicatorColor="secondary"
        value={value}
        onChange={handleChange}
        scrollButtons={false}
        className={styles.tabs}
      >
        <Tab
          className={styles.tab}
          component={Button}
          label={<TabLabel label="Source" disabled={!documentProcessed} />}
          disabled={!documentProcessed}
        />
        <Tab
          className={styles.tab}
          component={Button}
          label={<TabLabel label="New" disabled={!documentProcessed} />}
          disabled={!documentProcessed}
        />
      </Tabs>
      <div className={styles.overprint}>
        {value === OptionalContentTabIndexes.source ? (
          <OverprintSwitch documentType={DocumentTypes.source} />
        ) : (
          <OverprintSwitch documentType={DocumentTypes.target} />
        )}
      </div>
      <div className={`${styles.list} ${classes.listBorder} ${classes.scrollBar}`}>
        <GVTabPanel value={value} index={OptionalContentTabIndexes.source}>
          <OptionalContentList
            documentType={DocumentTypes.source}
            separationsEnabled={sourceSeparationsEnabled}
            handleSeparationsToggle={(enabled) => handleSeparationsToggle(enabled, DocumentTypes.source)}
          />
        </GVTabPanel>
        <GVTabPanel value={value} index={OptionalContentTabIndexes.new}>
          <OptionalContentList
            documentType={DocumentTypes.target}
            separationsEnabled={targetSeparationsEnabled}
            handleSeparationsToggle={(enabled) => handleSeparationsToggle(enabled, DocumentTypes.target)}
          />
        </GVTabPanel>
      </div>
    </div>
  );
};

export default OptionalContent;
